/**
 * Central React Query key factory.
 *
 * Every cached query must derive its key from here so mutations can
 * invalidate the right slice without grepping the codebase.
 *
 * Convention (per TkDodo's effective query keys pattern):
 *   keys.<resource>.all         -> base scope, used for broad invalidation
 *   keys.<resource>.lists()     -> any list query for this resource
 *   keys.<resource>.list(args)  -> a specific filtered list
 *   keys.<resource>.detail(id)  -> a single item
 *
 * Example:
 *   useQuery({ queryKey: keys.burgers.detail(id), queryFn: ... })
 *   qc.invalidateQueries({ queryKey: keys.burgers.lists() })
 */

export const keys = {
  burgers: {
    all: ["burgers"] as const,
    lists: () => [...keys.burgers.all, "list"] as const,
    list: (params: Record<string, unknown>) => [...keys.burgers.lists(), params] as const,
    detail: (id: string) => [...keys.burgers.all, "detail", id] as const,
    bySlug: (producerSlug: string, burgerSlug: string) =>
      [...keys.burgers.all, "slug", producerSlug, burgerSlug] as const,
    topForLanding: () => [...keys.burgers.all, "landing-top"] as const,
  },

  producers: {
    all: ["producers"] as const,
    lists: () => [...keys.producers.all, "list"] as const,
    detail: (id: string) => [...keys.producers.all, "detail", id] as const,
    bySlug: (slug: string) => [...keys.producers.all, "slug", slug] as const,
  },

  checkins: {
    all: ["checkins"] as const,
    lists: () => [...keys.checkins.all, "list"] as const,
    feed: (userId: string | null) => [...keys.checkins.all, "feed", userId] as const,
    byUser: (userId: string) => [...keys.checkins.all, "user", userId] as const,
    byBurger: (burgerId: string) => [...keys.checkins.all, "burger", burgerId] as const,
    detail: (id: string) => [...keys.checkins.all, "detail", id] as const,
    likes: (checkinId: string) => [...keys.checkins.all, "likes", checkinId] as const,
  },

  profiles: {
    all: ["profiles"] as const,
    detail: (idOrUsername: string) => [...keys.profiles.all, "detail", idOrUsername] as const,
    me: () => [...keys.profiles.all, "me"] as const,
    badges: (userId: string) => [...keys.profiles.all, "badges", userId] as const,
    stats: (userId: string) => [...keys.profiles.all, "stats", userId] as const,
  },

  follows: {
    all: ["follows"] as const,
    followers: (userId: string) => [...keys.follows.all, "followers", userId] as const,
    following: (userId: string) => [...keys.follows.all, "following", userId] as const,
    followingIds: (userId: string) => [...keys.follows.all, "following-ids", userId] as const,
    isFollowing: (followerId: string, followingId: string) =>
      [...keys.follows.all, "is", followerId, followingId] as const,
  },

  friends: {
    all: ["friends"] as const,
    list: (userId: string) => [...keys.friends.all, "list", userId] as const,
    requests: (userId: string) => [...keys.friends.all, "requests", userId] as const,
  },

  notifications: {
    all: ["notifications"] as const,
    list: (userId: string) => [...keys.notifications.all, "list", userId] as const,
    unreadCount: (userId: string) => [...keys.notifications.all, "unread", userId] as const,
  },

  leaderboards: {
    all: ["leaderboards"] as const,
    list: (params: Record<string, unknown>) => [...keys.leaderboards.all, "list", params] as const,
    directory: (params: Record<string, unknown>) =>
      [...keys.leaderboards.all, "directory", params] as const,
  },

  discover: {
    all: ["discover"] as const,
    feed: (params: Record<string, unknown>) => [...keys.discover.all, "feed", params] as const,
    newUsers: () => [...keys.discover.all, "new-users"] as const,
    suggestedUsers: (userId: string | null) =>
      [...keys.discover.all, "suggested-users", userId] as const,
  },

  map: {
    all: ["map"] as const,
    checkins: (params: Record<string, unknown>) => [...keys.map.all, "checkins", params] as const,
  },

  locations: {
    all: ["locations"] as const,
    search: (q: string) => [...keys.locations.all, "search", q] as const,
    stats: (locationId: string) => [...keys.locations.all, "stats", locationId] as const,
  },

  proposals: {
    all: ["proposals"] as const,
    forEntity: (entityType: string, entityId: string) =>
      [...keys.proposals.all, "entity", entityType, entityId] as const,
    forUser: (userId: string) => [...keys.proposals.all, "user", userId] as const,
  },
} as const;
